/**
 * Loads the dashboard translations one language at a time.
 *
 * `dashboard/i18n` imports every upstream language eagerly, which put ~17 MB of
 * translations in the chunk every page downloads. The entry points load only `en`
 * (the fallback) plus the language in use, and fetch another one when the UI
 * switches to it. `dashboard/i18n` stays as is for specs and Histoire.
 */

import { watch } from 'vue';
import { buildForkMessages, deepMerge } from 'shared/helpers/forkTranslations';

const FALLBACK_LOCALE = 'en';

// The languages `dashboard/i18n` registers. Not a glob: upstream ships locale
// folders it does not register, and some of them do not even build (`zh`).
const upstreamLoaders = {
  ar: () => import('../locale/ar'),
  bg: () => import('../locale/bg'),
  ca: () => import('../locale/ca'),
  cs: () => import('../locale/cs'),
  da: () => import('../locale/da'),
  de: () => import('../locale/de'),
  el: () => import('../locale/el'),
  en: () => import('../locale/en'),
  es: () => import('../locale/es'),
  et: () => import('../locale/et'),
  fa: () => import('../locale/fa'),
  fi: () => import('../locale/fi'),
  fr: () => import('../locale/fr'),
  he: () => import('../locale/he'),
  hi: () => import('../locale/hi'),
  hu: () => import('../locale/hu'),
  id: () => import('../locale/id'),
  it: () => import('../locale/it'),
  ja: () => import('../locale/ja'),
  ko: () => import('../locale/ko'),
  lv: () => import('../locale/lv'),
  ml: () => import('../locale/ml'),
  nl: () => import('../locale/nl'),
  no: () => import('../locale/no'),
  pl: () => import('../locale/pl'),
  pt: () => import('../locale/pt'),
  pt_BR: () => import('../locale/pt_BR'),
  ro: () => import('../locale/ro'),
  ru: () => import('../locale/ru'),
  sk: () => import('../locale/sk'),
  sl: () => import('../locale/sl'),
  sr: () => import('../locale/sr'),
  sv: () => import('../locale/sv'),
  ta: () => import('../locale/ta'),
  th: () => import('../locale/th'),
  tr: () => import('../locale/tr'),
  uk: () => import('../locale/uk'),
  uz: () => import('../locale/uz'),
  vi: () => import('../locale/vi'),
  zh_CN: () => import('../locale/zh_CN'),
  zh_TW: () => import('../locale/zh_TW'),
  is: () => import('../locale/is'),
  lt: () => import('../locale/lt'),
};
const forkLoaders = import.meta.glob('./locale/*/*.json', {
  import: 'default',
});

export const loadLocaleMessages = async locale => {
  const upstreamLoader = upstreamLoaders[locale];
  const forkPaths = Object.keys(forkLoaders).filter(
    path => path.split('/')[2] === locale
  );

  const [upstream, ...forkModules] = await Promise.all([
    upstreamLoader ? upstreamLoader().then(module => module.default) : {},
    ...forkPaths.map(path => forkLoaders[path]()),
  ]);
  const fork = buildForkMessages(
    Object.fromEntries(forkPaths.map((path, i) => [path, forkModules[i]])),
    () => locale
  );

  return deepMerge(upstream, fork[locale] ?? {});
};

/**
 * Loads `en` and `initialLocale` into the i18n instance and keeps loading
 * whatever language `i18n.global.locale` switches to afterwards. Resolves once
 * the initial languages are in, so the app can mount without an English flash.
 */
export const setupLazyLocaleMessages = (i18n, initialLocale) => {
  const loading = new Map();
  const ensureLoaded = locale => {
    if (!locale) return Promise.resolve();
    if (!loading.has(locale)) {
      loading.set(
        locale,
        loadLocaleMessages(locale).then(messages =>
          i18n.global.setLocaleMessage(locale, messages)
        )
      );
    }
    return loading.get(locale);
  };

  watch(i18n.global.locale, ensureLoaded);
  return Promise.all([
    ensureLoaded(FALLBACK_LOCALE),
    ensureLoaded(initialLocale),
  ]);
};
